import { useCallback, useEffect, useState } from 'react';

/**
 * Tema da interface: claro, escuro ou o do sistema.
 *
 * A escolha vive em duas camadas, e as duas importam:
 *
 *   `localStorage`   sobrevive ao fechar a aba. Lido por `/tema.js` antes da
 *                    primeira pintura, para não piscar claro
 *   `data-theme`     atributo no elemento raiz, que é o que o CSS consulta
 *
 * `sistema` é a AUSÊNCIA de atributo, não um terceiro valor. Assim quem nunca
 * tocou no botão acompanha o computador sozinho, inclusive quando ele muda de
 * tema ao anoitecer.
 */

const STORAGE_KEY = 'aihub.tema';

/** Nunca deixa uma falha de armazenamento derrubar a tela. */
function store(value) {
  try {
    if (value === 'sistema') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Janela anônima ou política corporativa. O tema vale para esta sessão e
    // não sobrevive ao recarregar — melhor que quebrar o botão.
  }
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : 'sistema';
  } catch {
    return 'sistema';
  }
}

export function useTheme() {
  const [theme, setTheme] = useState(readStored);

  // Reflete a escolha no DOM. Roda também na montagem, o que corrige o caso de
  // duas abas abertas: a que estava aberta não passou pelo `/tema.js` de novo.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'sistema') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  const choose = useCallback((value) => {
    store(value);
    setTheme(value);
  }, []);

  /**
   * Alterna entre claro e escuro.
   *
   * Estando em `sistema`, vai para o OPOSTO do que está na tela — e não para um
   * valor fixo. Clicar em "modo escuro" com o sistema já escuro e nada mudar
   * faria o botão parecer quebrado.
   */
  const toggle = useCallback(() => {
    const isDarkNow =
      theme === 'dark' ||
      (theme === 'sistema' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    choose(isDarkNow ? 'light' : 'dark');
  }, [theme, choose]);

  return { theme, choose, toggle };
}
